import {DOCUMENT} from '@angular/common';
import {Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NavigationEnd, Router, RouterEvent, RouterOutlet} from '@angular/router';

import {select, Store} from '@ngrx/store';
import {fromEvent, Observable, Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import * as GlobalMessageActions from './core/actions/global-message.actions';
import * as LayoutActions from './core/actions/layout.actions';
import {GlobalMessage} from './core/models/global-message';
import {Language} from './core/models/language';
import {LanguageService} from './core/services/language.service';
import * as fromQuotes from './quotes/reducers';
import * as QuoteActions from'./quotes/actions/quote.action';
import * as fromRoot from './reducers';
import {getGlobalMessages, getIsQuoteListOpen, getIsQuoteViewSelectionOpen, getIsSideNavOpen, getIsSiteMenuOpen} from './reducers';
import {WINDOW} from './shared/services/window.service';
import * as AuthActions from './user/actions/user.actions';
import * as fromAuth from './user/reducers';
import {LoginPageComponent} from './user/containers/login-page/login-page.component';
import {RegistrationPageComponent} from './user/containers/registration-page/registration-page.component';
import {RequestPasswordResetPageComponent} from './user/containers/request-password-reset-page/request-password-reset-page.component';
import {PasswordResetPageComponent} from './user/containers/password-reset-page/password-reset-page.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    isLoggedIn$: Observable<boolean>;
    hasNonEmptyQuotes$: Observable<boolean>;
    isSideNavOpen$: Observable<boolean>;
    isSiteMenuOpen$: Observable<boolean>;
    isQuoteViewSelectionOpen$: Observable<boolean>;
    isAdvancedSearchOpen$: Observable<boolean>;
    globalMessages$: Observable<GlobalMessage[]>;
    currentLanguage: Language;
    languages: Language[];

    activatedComponent$ = new EventEmitter<object>();
    navigationEnd$: Subscription;
    backdropClose$: Subscription;

    activatedComponentSubscription$: Subscription;

    anonymousPageShowing: boolean;

    @ViewChild(RouterOutlet, { static: true })
    routerOutlet: RouterOutlet;
    @ViewChild('backdrop', { static: true })
    backdropElement: ElementRef;

    constructor(private store: Store<fromRoot.State>,
                private languageService: LanguageService,
                private router: Router,
                @Inject(WINDOW) private window: Window,
                @Inject(DOCUMENT) private document: Document,
                private quoteStore: Store<fromQuotes.State>) {
    }


    get windowInnerWidth() {
        return this.window.innerWidth;
    }

    logout() {
        this.store.dispatch(AuthActions.logout({requestedUrl: null}));
    }

    openQuoteViewSelection() {
        this.store.dispatch(LayoutActions.openQuoteViewSelection());
        this.quoteStore.dispatch(QuoteActions.openQuoteListMenu())
    }

    closeQuoteViewSelection() {
        this.store.dispatch(LayoutActions.closeQuoteViewSelection());
        this.quoteStore.dispatch(QuoteActions.closeAllSideNavs())
    }

    openSiteMenu() {
        this.store.dispatch(LayoutActions.openSiteMenu());
    }

    closeAllSideNavs() {
        this.store.dispatch(QuoteActions.closeAllSideNavs());
    }

    closeSideNav() {
        this.closeAllSideNavs();
        this.closeQuoteViewSelection();
    }

    switchLanguage(language: Language) {
        console.log('Language service switch', language);
        this.languageService.switchLanguage(language);
    }

    dismissGlobalMessage(message: GlobalMessage) {
        this.store.dispatch(GlobalMessageActions.dismissMessage({globalMessage: message}));
    }

    ngOnInit(): void {
        this.isLoggedIn$ = this.store.pipe(select(fromAuth.getIsLoggedIn));
        this.hasNonEmptyQuotes$ = this.store.pipe(select(fromQuotes.getHasNonEmptyQuotes));
        this.isQuoteViewSelectionOpen$ = this.store.pipe(select(getIsQuoteViewSelectionOpen));
        this.isSiteMenuOpen$ = this.store.pipe(select(getIsSiteMenuOpen));
        this.isSideNavOpen$ = this.store.pipe(select(getIsSideNavOpen));
        this.globalMessages$ = this.store.pipe(select(getGlobalMessages));

        this.backdropClose$ = fromEvent(this.backdropElement.nativeElement, 'click')
            .subscribe(() => this.closeSideNav());

        const footerDisabledComponents = [
            LoginPageComponent.COMPONENT_ID,
            RegistrationPageComponent.COMPONENT_ID,
            RequestPasswordResetPageComponent.COMPONENT_ID,
            PasswordResetPageComponent.COMPONENT_ID
        ];

        this.activatedComponentSubscription$ = this.activatedComponent$
            .subscribe((component) => {
                this.anonymousPageShowing = footerDisabledComponents.includes((component as Navigable).getId());
            });

        // todo: later we could use the language as app state
        this.currentLanguage = this.languageService.getCurrentLanguage();
        this.languages = this.languageService.getLanguages();

        this.navigationEnd$ = this.router.events
            .pipe(filter<RouterEvent>(e => e instanceof NavigationEnd))
            .subscribe(() => {
                this.activatedComponent$.emit(this.routerOutlet.component);
            });

    }

    ngOnDestroy(): void {
        this.navigationEnd$.unsubscribe();
        this.backdropClose$.unsubscribe();
        this.activatedComponentSubscription$.unsubscribe();
    }
}

